/**
 * QL-277 — turn a client into the Trapets screening input.
 *
 * One set of rules for every caller: the advisor form passes its fields, and
 * `trapets-lookup-service` passes the client record and its acting
 * representative for a screening started from chat. Both therefore search
 * exactly the same thing for the same data.
 *
 * Pure on purpose, and it returns a reason code rather than a sentence: the form
 * shows a translated field error, the AI gets a hint naming the tool to fix it.
 */

import { screeningClientType } from "./compliance-review";
import type { TrapetsLookupInput, TrapetsRepresentativeInput } from "./flow-instances";
import { buildSSNWithPrefix, getISO3Code } from "./trapets";

/** The client columns a screening reads — the shape of `getClientScreeningFields`. */
export type ScreeningClientFields = {
  name: string | null;
  type: string | null;
  ssn: string | null;
  dateOfBirth: string | null;
  orgId: string | null;
  country: string | null;
};

/** The identity half of the QP-184 acting representative. */
export type ScreeningRepresentative = {
  /** `client_party.id` / flagged member id (QL-654), carried through to the saved lookup. */
  id?: string;
  name: string;
  ssn: string | null;
  dateOfBirth: string | null;
  country: string | null;
};

/**
 * What a caller may supply on top of the client record — only what no other
 * tool can set. Name, national ID, org number and country are corrected on the
 * client itself.
 */
export type TrapetsScreeningOverrides = {
  /** Fills a gap only: ignored when the record has a national ID or a date of birth. */
  birthDate?: string | null;
  /**
   * The representative to screen, taken as a unit: the name and at least one
   * identifier must come together. It replaces the registered representative as
   * the SEARCH input only - the lookup refuses a person who is not a registered
   * representative (once the client has any), and never edits a registered one.
   */
  representativeName?: string | null;
  representativeSsn?: string | null;
  representativeBirthDate?: string | null;
  representativeCountry?: string | null;
  /**
   * QL-654 — several representatives at once, instead of all the registered ones
   * (same rule as the single fields above). Each needs a name and a national ID or
   * a date of birth.
   */
  representatives?: Array<{
    name?: string | null;
    ssn?: string | null;
    birthDate?: string | null;
    country?: string | null;
  }>;
};

/**
 * Why no input could be built. `missing_person_id` = neither a national ID nor a
 * date of birth. `incomplete_representative_override` = a representative override
 * was passed without both a name and an identifier.
 */
export type TrapetsScreeningInputError =
  | "missing_name"
  | "missing_country"
  | "missing_person_id"
  | "missing_representative"
  | "incomplete_representative_override";

export type TrapetsScreeningInputResult =
  | { ok: true; input: TrapetsLookupInput }
  | { ok: false; reason: TrapetsScreeningInputError };

const clean = (value: string | null | undefined): string | null => value?.trim() || null;

export function buildTrapetsScreeningInput(
  client: ScreeningClientFields,
  /** QL-654 — every registered representative, in order (empty = none). */
  registered: readonly ScreeningRepresentative[],
  overrides: TrapetsScreeningOverrides = {},
): TrapetsScreeningInputResult {
  // Group stays distinct: `suitabilityClientType` folds it into Person, which
  // would screen a natural person under the group's name.
  const clientType = screeningClientType(client.type);
  const name = clean(client.name);
  const country = clean(client.country)?.toUpperCase() ?? null;

  // The column is NOT NULL but may hold only whitespace. The result is filed
  // under this name, and a Group (screened through its representative) or a
  // Person with an SSN would otherwise be screened and saved under nobody.
  if (!name) return { ok: false, reason: "missing_name" };

  if (!country) return { ok: false, reason: "missing_country" };

  const input: TrapetsLookupInput = { clientType, name, country };

  if (clientType === "Person") {
    // National ID first, then date of birth; only the chosen one is emitted.
    // The override fills a gap — it never replaces what the record says.
    const ssn = clean(client.ssn);
    const birthDate = clean(client.dateOfBirth) ?? clean(overrides.birthDate);
    if (ssn) {
      input.ssn = ssn;
      input.ssnWithPrefix = buildSSNWithPrefix(country, ssn);
    } else if (birthDate) {
      input.birthDate = birthDate;
    } else {
      return { ok: false, reason: "missing_person_id" };
    }
    return { ok: true, input };
  }

  if (clientType === "Company") {
    // A Group has no org number of its own, so it gets no entity search.
    const orgId = clean(client.orgId);
    if (orgId) input.orgId = orgId;
    input.iso3Country = getISO3Code(country);
  }

  // The representatives are taken whole: either the registered ones, or — when
  // any identity override is passed — the overrides alone. Mixing them field by
  // field would pair a new name with the old person's national ID, which wins
  // over a date of birth: Trapets would search the old person under the new
  // name, and the sync would write that hybrid back to client_party.
  const overrideList = overrides.representatives ?? [];
  const singleOverride = {
    name: clean(overrides.representativeName),
    ssn: clean(overrides.representativeSsn),
    birthDate: clean(overrides.representativeBirthDate),
    country: clean(overrides.representativeCountry),
  };
  const singleOverridden = Boolean(
    singleOverride.name || singleOverride.ssn || singleOverride.birthDate,
  );
  const overridden = overrideList.length > 0 || singleOverridden;

  const cleanedOverrides = [
    ...(singleOverridden ? [singleOverride] : []),
    ...overrideList.map((r) => ({
      name: clean(r.name),
      ssn: clean(r.ssn),
      birthDate: clean(r.birthDate),
      country: clean(r.country),
    })),
  ];
  if (overridden && !cleanedOverrides.every((r) => r.name && (r.ssn || r.birthDate))) {
    return { ok: false, reason: "incomplete_representative_override" };
  }

  // An overridden representative may be someone else, so their country is never
  // borrowed from the registered one.
  const people: Array<{
    name: string | null;
    ssn: string | null;
    birthDate: string | null;
    country: string;
    partyId?: string;
  }> = overridden
    ? cleanedOverrides.map((r) => ({ ...r, country: r.country?.toUpperCase() ?? country }))
    : registered.map((r, index) => ({
        name: clean(r.name),
        ssn: clean(r.ssn),
        birthDate: clean(r.dateOfBirth),
        // A country override alone re-points the (first) registered
        // representative's search without replacing who they are.
        country:
          ((index === 0 ? singleOverride.country : null) ?? clean(r.country))?.toUpperCase() ??
          country,
        ...(r.id ? { partyId: r.id } : {}),
      }));

  // Every one of them has to be screenable: one representative left out would
  // let the lookup pass on the strength of the others.
  if (people.length === 0 || !people.every((r) => r.name && (r.ssn || r.birthDate))) {
    return { ok: false, reason: "missing_representative" };
  }

  const representatives: TrapetsRepresentativeInput[] = people.map((r) => ({
    name: r.name as string,
    ...(r.ssn ? { ssn: r.ssn } : { birthDate: r.birthDate as string }),
    country: r.country,
    ...(r.partyId ? { partyId: r.partyId } : {}),
  }));
  input.representatives = representatives;

  // The single-representative shorthand mirrors the first, for readers that
  // predate the list.
  const first = representatives[0] as TrapetsRepresentativeInput;
  input.representativeName = first.name;
  if (first.ssn) input.representativeSsn = first.ssn;
  else if (first.birthDate) input.representativeBirthDate = first.birthDate;
  input.representativeCountry = first.country;

  return { ok: true, input };
}
